import {
  rdbV1FiltersYearsList,
  rdbV2UsersPredictionsDetail,
  type RdbV2UsersPredictionsDetailParams,
  type RpmPickListPredictionUserProfileResponseRecordRelatedPagedData,
  type SportFilter,
  v1AuthorsDetail,
} from '@on3/api';
import type { IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import withAuthentication from '@on3/ui-lib/src/hocs/withAuthentication';
import withValidUrl from '@on3/ui-lib/src/hocs/withValidUrl';
import { baseSports } from '@on3/ui-lib/store/mocks/sports';
import { getFulfilledValue, proxyApi } from '@on3/ui-lib/utils/api';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { PageWrapper } from 'components/Layouts/PageWrapper';
import { RightSidebarLayout } from 'components/Layouts/RightSidebarLayout';
import { StandardSidebar } from 'components/Layouts/StandardSidebar';
import { PredictionList } from 'components/Writers/Prediction/PredictionList';
import type { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';

export interface IPredictionPageProps extends IGenericPageProps {
  pickData?: RpmPickListPredictionUserProfileResponseRecordRelatedPagedData | null;
  title: string;
  filters: {
    sports: SportFilter[];
    years: number[];
  };
}

const PredictionPage = (props: IPredictionPageProps) => {
  const title = `${props?.pickData?.relatedModel?.userRecord?.name} Expert Predictions`;
  const router = useRouter();
  const { sport, year } = router.query;

  return (
    <PageWrapper {...props} title={title}>
      <RightSidebarLayout>
        <PredictionList {...props} key={`${sport}-${year}`} title={title} />
        <StandardSidebar />
      </RightSidebarLayout>
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const siteData = await withValidUrl.isValid(ctx);
  const userData = await withAuthentication.isAuth(ctx);

  if (!siteData) {
    return {
      notFound: true,
    };
  }

  const {
    slug,
    sport,
    year = 'all',
    page = 1,
  } = ctx.query as {
    slug: string;
    sport?: string;
    page?: string;
    year?: string;
  };

  try {
    const { sportsData } = siteData || {};
    const sportKey = sportsData?.find(
      (sp) => sp?.name && slugify(sp?.name) === sport,
    )?.key;

    const filterYear = year === 'all' ? undefined : +year;

    const [expertData] = await Promise.allSettled([
      v1AuthorsDetail(proxyApi, slug),
    ]);
    const fetchedExpert = getFulfilledValue(expertData);

    if (!expertData) {
      return {
        notFound: true,
      };
    }

    const params: RdbV2UsersPredictionsDetailParams = {
      sportKey,
      year: filterYear,
      page: +page,
      userKey: fetchedExpert?.author?.key || 0,
    };

    const [pickData, years] = await Promise.allSettled([
      rdbV2UsersPredictionsDetail(proxyApi, params),
      rdbV1FiltersYearsList(proxyApi),
    ]);

    const data = {
      pageType: 'database',
      siteData,
      userData: userData || {},
      expertData: fetchedExpert,
      pickData: getFulfilledValue(pickData),
      filters: {
        years: getFulfilledValue(years),
        sports: baseSports,
      },
    };

    return { props: { ...data } };
  } catch (e) {
    return { props: [] };
  }
};

export default withValidUrl(PredictionPage);
